import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

/** CB2 preview phase for {@link UserFeedbackBlockComponent}. */
export type UserFeedbackBlockScenarioPhase = 'initial' | 'positive' | 'negative';

/**
 * Serializable preview VM for `UserFeedbackBlockComponent`.
 * Controls whether the post-rating share modal is shown in CB2 studio preview.
 */
export type UserFeedbackBlockScenarioContent = {
  phase: UserFeedbackBlockScenarioPhase;
};

export const USERFEEDBACKBLOCK_LEGACY_FIELD_ORDER = [
  "title",
  "min_value_label",
  "max_value_label",
  "background_color",
  "text_color",
  "positive_rating_threshold",
  { type: 'separator' },
  { type: 'heading', value: "Ecran affiché si la note est bonne" },
  "modal_positive_rating_title",
  "modal_positive_rating_message",
  { type: 'separator' },
  { type: 'heading', value: "Ecran affiché si la note est mauvaise" },
  "modal_negative_rating_title",
  "modal_negative_rating_message"
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  userFeedbackBlockDescriptor: _generatedUserFeedbackBlockDescriptor,
  userFeedbackBlockBlocDefinition,
  userFeedbackBlockDashboardLayout,
  userFeedbackBlockConfig,
  userFeedbackBlockResolveConfig,
  userFeedbackBlockProperties,
  userFeedbackBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "User feedback",
    componentType: 'UserFeedbackBlockComponent',
    category: 'content',
    description: "User feedback.",
    keywords: [],
    sharedFields: {},
    slots: [
      {
        key: 'modal_positive_rating_content',
        name: "pages.blocks.user-feedback.modal-positive-rating-content.label",
        accept: 'explicitOnly',
        allowedTypes: ["UserFeedbackLinkBlock", "ExternalLinkButtonBlock", "ModalButtonBlock"],
        slotItemFormat: 'ids',
      },
      {
        key: 'modal_negative_rating_content',
        name: "pages.blocks.user-feedback.modal-negative-rating-content.label",
        accept: 'explicitOnly',
        allowedTypes: ["UserFeedbackLinkBlock", "ExternalLinkButtonBlock", "ModalButtonBlock"],
        slotItemFormat: 'ids',
      }
    ]
  },

  () => ({
    title: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.user-feedback.title.label",
        max_length: 100,
        placeholder: "pages.blocks.user-feedback.title.placeholder",
      }
    }),
    min_value_label: propertyValue({
      type: 'text',
      value: '',
      options: {
        max_length: 100,
        label: "pages.blocks.user-feedback.min-value-label.label",
        placeholder: "pages.blocks.user-feedback.min-value-label.placeholder",
      }
    }),
    max_value_label: propertyValue({
      type: 'text',
      value: '',
      options: {
        max_length: 100,
        label: "pages.blocks.user-feedback.max-value-label.label",
        placeholder: "pages.blocks.user-feedback.max-value-label.placeholder",
      }
    }),
    background_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"primary-600"},
      options: {
        label: "pages.blocks.user-feedback.background-color.label",
      }
    }),
    text_color: propertyValue({
      type: 'color',
      value: {"type":"palette","value":"primary-50"},
      options: {
        label: "pages.blocks.user-feedback.text-color.label",
      }
    }),
    positive_rating_threshold: propertyValue({
      type: 'number',
      value: 3.5,
      options: {
        max_length: 5,
        label: "pages.blocks.user-feedback.positive-rating-threshold.label",
        help: "pages.blocks.user-feedback.positive-rating-threshold.help",
      }
    }),
    modal_positive_rating_title: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.user-feedback.modal-positive-rating-title.label",
        placeholder: "pages.blocks.user-feedback.modal-positive-rating-title.placeholder",
      }
    }),
    modal_positive_rating_message: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.user-feedback.modal-positive-rating-message.label",
        placeholder: "pages.blocks.user-feedback.modal-positive-rating-message.placeholder",
      }
    }),
    modal_negative_rating_title: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.user-feedback.modal-negative-rating-title.label",
        placeholder: "pages.blocks.user-feedback.modal-negative-rating-title.placeholder",
      }
    }),
    modal_negative_rating_message: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.user-feedback.modal-negative-rating-message.label",
        placeholder: "pages.blocks.user-feedback.modal-negative-rating-message.placeholder",
      }
    })
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.user-feedback.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.user-feedback.fieldset.rating.label",
            fields: [
              "title",
              "min_value_label",
              "max_value_label",
              "positive_rating_threshold",
              "background_color",
              "text_color",
            ],
          },
          {
            legend: "pages.blocks.user-feedback.fieldset.positive-screen.label",
            fields: [
              "modal_positive_rating_title",
              "modal_positive_rating_message",
              "modal_positive_rating_content",
            ],
          },
          {
            legend: "pages.blocks.user-feedback.fieldset.negative-screen.label",
            fields: [
              "modal_negative_rating_title",
              "modal_negative_rating_message",
              "modal_negative_rating_content",
            ],
          },
        ],
      },
    ],
  })
);

export function userFeedbackBlockDescriptor(): IBlockDescriptor;
export function userFeedbackBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function userFeedbackBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedUserFeedbackBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedUserFeedbackBlockDescriptor, userFeedbackBlockProperties, fieldOrder);
}

export {
  userFeedbackBlockBlocDefinition,
  userFeedbackBlockDashboardLayout,
  userFeedbackBlockProperties,
  userFeedbackBlockConfig,
  userFeedbackBlockResolveConfig,
  userFeedbackBlockDefinitionSetupScenarios,
};

export type UserFeedbackBlockConfig = typeof userFeedbackBlockConfig;
